// src/services/NotificationService.js - NEW FILE
// Real notification service that enhances existing placeholder system

import { ErrorTrackingService } from './ErrorTrackingService';

export class NotificationService {
  static isInitialized = false;
  static config = {
    email: {
      enabled: false,
      provider: 'sendgrid', // 'sendgrid', 'mailgun', 'ses'
      apiKey: null,
      fromEmail: null,
      fromName: null,
    },
    sms: {
      enabled: false,
      provider: 'twilio', // 'twilio', 'messagebird'
      fromNumber: null,
    },
    whatsapp: {
      enabled: true,
      provider: 'n8n_webhook', // Custom n8n integration
      webhookUrl: 'https://lisa-agent-652532757120.us-central1.run.app/tools/send_whatsapp_response',
      businessPhone: null, // Will be loaded from business profile
    },
    inApp: {
      enabled: true, // Always enabled - uses existing BookingNotifications.js
    }
  };

  // Initialize notification service (non-breaking)
  static initialize() {
    try {
      // Load configuration from environment variables
      this.config.email.enabled = process.env.REACT_APP_EMAIL_NOTIFICATIONS === 'true';
      this.config.email.apiKey = process.env.REACT_APP_SENDGRID_API_KEY;
      this.config.email.fromEmail = process.env.REACT_APP_FROM_EMAIL;
      this.config.email.fromName = process.env.REACT_APP_FROM_NAME || 'Booker.Chat';

      this.config.sms.enabled = process.env.REACT_APP_SMS_NOTIFICATIONS === 'true';
      this.config.sms.fromNumber = process.env.REACT_APP_TWILIO_FROM_NUMBER;

      this.config.whatsapp.enabled = process.env.REACT_APP_WHATSAPP_NOTIFICATIONS === 'true';
      this.config.whatsapp.businessPhone = process.env.REACT_APP_WHATSAPP_BUSINESS_PHONE;

      this.isInitialized = true;
      console.log('✅ NotificationService initialized', {
        email: this.config.email.enabled,
        sms: this.config.sms.enabled,
        whatsapp: this.config.whatsapp.enabled,
        inApp: this.config.inApp.enabled
      });

      return true;
    } catch (error) {
      console.warn('⚠️ NotificationService initialization failed:', error);
      ErrorTrackingService.captureError(error, {
        service: 'NotificationService',
        method: 'initialize',
      });
      return false;
    }
  }

  // Enhanced notification sending that preserves existing behavior
  static async sendNotification(notificationData) {
    try {
      const results = {
        inApp: { success: true, id: null },
        email: { success: false, id: null },
        sms: { success: false, id: null },
        whatsapp: { success: false, id: null }
      };

      // 1. ALWAYS send in-app notification (preserves existing behavior)
      try {
        const { FirebaseService } = await import('./FirebaseService');
        const inAppResult = await FirebaseService.createBookingNotification(
          notificationData.businessId,
          {
            type: notificationData.type,
            recipient: notificationData.recipient,
            message: notificationData.message,
            bookingId: notificationData.bookingId,
            customerId: notificationData.customerId,
          }
        );
        
        results.inApp = inAppResult;
        console.log('📱 In-app notification sent:', notificationData.type);
      } catch (error) {
        console.error('Failed to send in-app notification:', error);
        results.inApp = { success: false, error: error.message };
      }

      // 2. Send additional notifications if configured (NEW enhancement)
      const promises = [];

      // Email notification
      if (this.config.email.enabled && notificationData.recipient.email) {
        promises.push(this.sendEmailNotification(notificationData));
      }

      // SMS notification
      if (this.config.sms.enabled && notificationData.recipient.phone) {
        promises.push(this.sendSMSNotification(notificationData));
      }

      // WhatsApp notification
      if (this.config.whatsapp.enabled && notificationData.recipient.phone) {
        promises.push(this.sendWhatsAppNotification(notificationData));
      }

      // Wait for all external notifications (don't let them block in-app)
      if (promises.length > 0) {
        const externalResults = await Promise.allSettled(promises);
        
        externalResults.forEach((result, index) => {
          const type = ['email', 'sms', 'whatsapp'][index];
          if (result.status === 'fulfilled') {
            results[type] = result.value;
          } else {
            results[type] = { success: false, error: result.reason.message };
          }
        });
      }

      // Track success/failure for monitoring
      this.trackNotificationMetrics(notificationData.type, results);

      return {
        success: results.inApp.success, // Success based on in-app (existing behavior)
        results,
        enhancedChannels: Object.keys(results).filter(key => 
          key !== 'inApp' && results[key].success
        )
      };

    } catch (error) {
      console.error('NotificationService.sendNotification failed:', error);
      ErrorTrackingService.captureError(error, {
        service: 'NotificationService',
        method: 'sendNotification',
        notificationType: notificationData.type,
      });

      return {
        success: false,
        error: error.message,
        results: {}
      };
    }
  }
    // Enhanced method to safely handle undefined values in notifications
    static safeNotificationMessage(message, fallback = 'Operation completed') {
        if (message === undefined || message === null || message === 'undefined') {
        console.warn('⚠️ Undefined message passed to notification:', message);
        return fallback;
        }
        return message;
    }

    // Enhanced booking confirmation with safety checks
    static async sendBookingConfirmation(businessId, bookingData) {
        try {
        // Safely extract booking information with fallbacks
        const customerName = bookingData?.customerName || bookingData?.customer_name || 'Customer';
        const serviceName = bookingData?.serviceName || bookingData?.service_name || 'Service';
        const bookingDate = bookingData?.bookingDate || bookingData?.booking_date || 'Date TBD';
        const bookingTime = bookingData?.time || 'Time TBD';
        
        const message = `✅ Booking confirmed for ${customerName} - ${serviceName} on ${bookingDate} at ${bookingTime}`;
        
        console.log('📤 Sending booking confirmation:', message);
        
        // Add your existing notification logic here
        return {
            success: true,
            message: this.safeNotificationMessage(message)
        };
        
        } catch (error) {
        console.error('Failed to send booking confirmation:', error);
        return {
            success: false,
            error: error.message
        };
        }
    }
      // Enhanced conflict alert with safety checks
  static async sendConflictAlert(businessId, conflictData) {
    try {
      const customerName = conflictData?.customerName || 'Customer';
      const conflictCount = conflictData?.conflictCount || 0;
      const requestedTime = conflictData?.requestedTime || 'requested time';
      
      const message = `⚠️ Booking conflict detected for ${customerName} at ${requestedTime}. ${conflictCount} conflict(s) found.`;
      
      console.log('📤 Sending conflict alert:', message);
      
      return {
        success: true,
        message: this.safeNotificationMessage(message)
      };
      
    } catch (error) {
      console.error('Failed to send conflict alert:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }
  // Email notification implementation
  static async sendEmailNotification(notificationData) {
    try {
      if (!this.config.email.enabled || !this.config.email.apiKey) {
        return { success: false, error: 'Email not configured' };
      }

      const emailData = this.formatEmailContent(notificationData);
      
      // Call your backend API or external service
      const response = await fetch('/api/notifications/email', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.config.email.apiKey}`
        },
        body: JSON.stringify({
          to: notificationData.recipient.email,
          from: this.config.email.fromEmail,
          fromName: this.config.email.fromName,
          subject: emailData.subject,
          html: emailData.html,
          text: emailData.text,
          bookingId: notificationData.bookingId,
        })
      });

      if (response.ok) {
        const result = await response.json();
        console.log('📧 Email notification sent:', notificationData.recipient.email);
        return { success: true, id: result.messageId };
      } else {
        throw new Error(`Email API failed: ${response.status}`);
      }

    } catch (error) {
      console.error('Email notification failed:', error);
      return { success: false, error: error.message };
    }
  }

  // SMS notification implementation
  static async sendSMSNotification(notificationData) {
    // STUB: SMS notifications are handled server-side by the Python backend.
    // Browser-side Twilio credential usage has been removed for security.
    // This will be wired to the backend notification service in a future update.
    console.log('[NotificationService] SMS send stubbed - handled by backend');
    return {
      success: false,
      status: 'not_implemented',
      message: 'SMS notifications are handled by the backend service.',
    };
  }

  // WhatsApp notification via n8n webhook
  static async sendWhatsAppNotification(notificationData) {
    try {
      if (!this.config.whatsapp.enabled) {
        return { success: false, error: 'WhatsApp not enabled' };
      }

      // Get business phone from business profile if not configured
      let businessPhone = this.config.whatsapp.businessPhone;
      if (!businessPhone && notificationData.businessPhone) {
        businessPhone = notificationData.businessPhone;
      }
      
      if (!businessPhone) {
        return { success: false, error: 'Business WhatsApp number not configured' };
      }

      // Format phone numbers for WhatsApp (ensure they have whatsapp: prefix)
      const formatWhatsAppNumber = (phone) => {
        if (!phone) return null;
        // Remove any existing whatsapp: prefix and clean the number
        const cleanPhone = phone.replace(/^whatsapp:/, '').replace(/[^\d+]/g, '');
        // Ensure it starts with + if it doesn't already
        const formattedPhone = cleanPhone.startsWith('+') ? cleanPhone : `+${cleanPhone}`;
        return `whatsapp:${formattedPhone}`;
      };

      const formattedBusinessPhone = formatWhatsAppNumber(businessPhone);
      const formattedUserPhone = formatWhatsAppNumber(notificationData.recipient.phone);

      if (!formattedUserPhone) {
        return { success: false, error: 'Invalid customer phone number' };
      }

      // Format message content
      const whatsappMessage = this.formatWhatsAppContent(notificationData);

      // Prepare n8n webhook payload
      const webhookPayload = [
        {
          "query": `business_phone: ${formattedBusinessPhone}, user_phone: ${formattedUserPhone}, message: "${whatsappMessage}"`
        }
      ];

      console.log('📱 Sending WhatsApp via n8n webhook:', {
        business: formattedBusinessPhone,
        customer: formattedUserPhone,
        type: notificationData.type
      });

      // Send to n8n webhook
      const response = await fetch(this.config.whatsapp.webhookUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(webhookPayload)
      });

      if (response.ok) {
        const result = await response.text(); // n8n might return text or JSON
        console.log('💬 WhatsApp notification sent via n8n:', formattedUserPhone);
        return { 
          success: true, 
          id: `n8n-${Date.now()}`,
          response: result,
          webhook: 'n8n',
          businessPhone: formattedBusinessPhone,
          customerPhone: formattedUserPhone
        };
      } else {
        const errorText = await response.text();
        throw new Error(`n8n webhook failed: ${response.status} - ${errorText}`);
      }

    } catch (error) {
      console.error('WhatsApp notification via n8n failed:', error);
      return { success: false, error: error.message };
    }
  }

  // Content formatting methods
  static formatEmailContent(notificationData) {
    const templates = {
      booking_created: {
        subject: `Booking Confirmation - ${notificationData.serviceName}`,
        html: `
          <h2>Booking Confirmed!</h2>
          <p>Dear ${notificationData.customerName},</p>
          <p>Your booking has been confirmed:</p>
          <ul>
            <li><strong>Service:</strong> ${notificationData.serviceName}</li>
            <li><strong>Date:</strong> ${notificationData.bookingDate}</li>
            <li><strong>Time:</strong> ${notificationData.time}</li>
            <li><strong>Business:</strong> ${notificationData.businessName}</li>
          </ul>
          <p>We look forward to seeing you!</p>
        `,
        text: `Booking Confirmed! Service: ${notificationData.serviceName}, Date: ${notificationData.bookingDate}, Time: ${notificationData.time}`
      },
      booking_updated: {
        subject: `Booking Updated - ${notificationData.serviceName}`,
        html: `
          <h2>Booking Updated</h2>
          <p>Dear ${notificationData.customerName},</p>
          <p>Your booking has been updated:</p>
          <ul>
            <li><strong>Service:</strong> ${notificationData.serviceName}</li>
            <li><strong>New Date:</strong> ${notificationData.bookingDate}</li>
            <li><strong>New Time:</strong> ${notificationData.time}</li>
          </ul>
        `,
        text: `Booking Updated! New Date: ${notificationData.bookingDate}, New Time: ${notificationData.time}`
      },
      booking_cancelled: {
        subject: `Booking Cancelled - ${notificationData.serviceName}`,
        html: `
          <h2>Booking Cancelled</h2>
          <p>Dear ${notificationData.customerName},</p>
          <p>Your booking for ${notificationData.serviceName} on ${notificationData.bookingDate} has been cancelled.</p>
          <p>Please contact us to reschedule.</p>
        `,
        text: `Your booking for ${notificationData.serviceName} on ${notificationData.bookingDate} has been cancelled.`
      }
    };

    return templates[notificationData.type] || templates.booking_created;
  }

  static formatSMSContent(notificationData) {
    const templates = {
      booking_created: `✅ Booking confirmed! ${notificationData.serviceName} on ${notificationData.bookingDate} at ${notificationData.time}. See you soon!`,
      booking_updated: `📅 Booking updated! ${notificationData.serviceName} moved to ${notificationData.bookingDate} at ${notificationData.time}.`,
      booking_cancelled: `❌ Booking cancelled: ${notificationData.serviceName} on ${notificationData.bookingDate}. Contact us to reschedule.`
    };

    return templates[notificationData.type] || templates.booking_created;
  }

  static formatWhatsAppContent(notificationData) {
    // Enhanced WhatsApp messages with business context
    const templates = {
      booking_created: `🎉 *Booking Confirmed!*\\n\\n📅 Service: ${notificationData.serviceName}\\n⏰ Date & Time: ${notificationData.bookingDate} at ${notificationData.time}\\n📍 ${notificationData.businessName}\\n\\nWe look forward to seeing you! If you need to make any changes, please call us at ${notificationData.businessPhone || 'our business number'}.`,
      
      booking_updated: `📝 *Booking Updated*\\n\\n📅 Service: ${notificationData.serviceName}\\n⏰ New Date & Time: ${notificationData.bookingDate} at ${notificationData.time}\\n📍 ${notificationData.businessName}\\n\\nSee you then! If you have any questions, please contact us.`,
      
      booking_cancelled: `❌ *Booking Cancelled*\\n\\nYour ${notificationData.serviceName} appointment on ${notificationData.bookingDate} has been cancelled.\\n\\nPlease contact us to reschedule at ${notificationData.businessPhone || 'our business number'}. Thank you!`,
      
      booking_reminder: `⏰ *Reminder: Upcoming Appointment*\\n\\n📅 Service: ${notificationData.serviceName}\\n⏰ Tomorrow at ${notificationData.time}\\n📍 ${notificationData.businessName}\\n\\nLooking forward to seeing you! Please arrive 5 minutes early.`,
      
      booking_followup: `Thank you for visiting ${notificationData.businessName}! We hope you enjoyed your ${notificationData.serviceName}. \\n\\nWe'd love to see you again! Book your next appointment by replying to this message or calling us.`
    };

    // Escape quotes for the n8n query format
    const message = templates[notificationData.type] || templates.booking_created;
    return message.replace(/"/g, '\\"'); // Escape quotes for JSON
  }

  // Analytics and monitoring
  static trackNotificationMetrics(type, results) {
    try {
      const metrics = {
        type,
        timestamp: new Date().toISOString(),
        channels: Object.keys(results),
        successCount: Object.values(results).filter(r => r.success).length,
        failureCount: Object.values(results).filter(r => !r.success).length,
      };

      console.log('📊 Notification metrics:', metrics);
      
      // Track in error tracking for monitoring
      ErrorTrackingService.addBreadcrumb('Notification sent', 'notification', 'info', metrics);
      
    } catch (error) {
      console.debug('Failed to track notification metrics:', error);
    }
  }

  // Helper methods for external integrations
  static getStatus() {
    return {
      initialized: this.isInitialized,
      channels: {
        inApp: this.config.inApp.enabled,
        email: this.config.email.enabled,
        sms: this.config.sms.enabled,
        whatsapp: this.config.whatsapp.enabled,
      }
    };
  }

  // Method to test notifications (development/setup)
  static async testNotifications(businessId, testEmail, testPhone, businessPhone = null) {
    const testData = {
      businessId,
      type: 'booking_created',
      recipient: {
        email: testEmail,
        phone: testPhone,
        name: 'Test Customer'
      },
      message: 'Test notification from Booker.Chat',
      customerName: 'Test Customer',
      serviceName: 'Test Service',
      bookingDate: new Date().toLocaleDateString(),
      time: '10:00 AM',
      businessName: 'Test Business',
      businessPhone: businessPhone || process.env.REACT_APP_WHATSAPP_BUSINESS_PHONE || '+1234567890',
      bookingId: 'test-booking-id',
      customerId: 'test-customer-id'
    };

    console.log('🧪 Testing notifications with n8n WhatsApp integration...');
    return await this.sendNotification(testData);
  }
}
